import { Building2, Calendar, Landmark } from 'lucide-react';
import { workStatusLabel } from '../firm/companyIntakeFields';
import { CLOSING_DATE_COLORS, closingDateVariant } from '../../utils/closingDate';
import { EditableClosingDateChip } from './EditableClosingDateChip';

/** Formatted closing date for banners (matches client analyses + firm company detail). */
export function formatClosingDateBanner(iso: string | undefined | null): string {
  const raw = (iso ?? '').trim();
  if (!raw) return '—';
  const d = new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Closing + deal workflow status pills (shared with {@link CompanySummaryCard}). */
export function CompanyIntakeBadges({
  closingDate,
  workStatus,
  className = '',
  onClosingDateSave,
  closingDateEditable = false,
}: {
  closingDate?: string | null;
  workStatus?: string | null;
  className?: string;
  onClosingDateSave?: (isoYmd: string) => Promise<void>;
  closingDateEditable?: boolean;
}) {
  const workStatusIn = workStatus ?? undefined;
  const closingRaw = (closingDate ?? '').trim();
  const closingColorClass = closingRaw ? CLOSING_DATE_COLORS[closingDateVariant(closingRaw || null)] : 'text-gray-500';

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`.trim()}>
      {closingDateEditable && onClosingDateSave ? (
        <EditableClosingDateChip closingDate={closingDate} onSave={onClosingDateSave} variant="intake" />
      ) : (
        <span
          className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-white/70 px-2 py-1 text-xs shadow-sm ring-1 ring-gray-200/70"
          title="Expected deal closing date"
        >
          <span className="inline-flex shrink-0 text-emerald-600" title="Closing date">
            <Calendar size={13} strokeWidth={2} aria-hidden />
          </span>
          <span className="text-gray-500">Closing</span>
          <span className={`tabular-nums ${closingColorClass}`}>{formatClosingDateBanner(closingDate)}</span>
        </span>
      )}
      {(() => {
        const v = (workStatusIn ?? '').trim();
        const statusValueClass = (() => {
          switch (v) {
            case 'in_progress':
              return 'text-blue-900';
            case 'review':
              return 'text-violet-900';
            case 'complete':
              return 'text-emerald-900';
            case 'on_hold':
              return 'text-amber-900';
            case 'intake':
            default:
              return 'text-slate-800';
          }
        })();

        return (
          <span
            className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-white/70 px-2 py-1 text-xs shadow-sm ring-1 ring-gray-200/70"
            title="Overall deal / 280G workflow"
          >
            <span className="text-gray-500">Status</span>
            <span className={`tabular-nums ${statusValueClass}`}>{workStatusLabel(workStatusIn)}</span>
          </span>
        );
      })()}
    </div>
  );
}

export type CompanySummaryCardProps = {
  companyName: string;
  closingDate?: string | null;
  workStatus?: string | null;
  dealStructure?: string | null;
  dealRole?: string | null;
  listingStatus?: string | null;
  lawFirmName?: string | null;
  /** e.g. firm “Edit company” button next to the title */
  titleAfter?: JSX.Element | null;
  notes?: string | null;
  className?: string;
};

/**
 * Shared company intake summary (building icon, name, closing, workflow status, law firm, deal structure / role / listing).
 * Used on the client analyses list and the firm company detail page.
 */
export default function CompanySummaryCard({
  companyName,
  closingDate,
  workStatus,
  dealStructure: _dealStructure,
  dealRole: _dealRole,
  listingStatus: _listingStatus,
  lawFirmName,
  titleAfter,
  notes,
  className = '',
}: CompanySummaryCardProps) {
  const notesTrim = notes != null ? notes.trim() : '';
  const showNotes = notesTrim.length > 0;

  return (
    <div
      className={`overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-sm ring-1 ring-gray-100/80 ${className}`.trim()}
    >
      <div className="relative border-b border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-slate-50/40 px-3 py-2.5 sm:px-4 sm:py-3">
        <div className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-emerald-500 sm:inset-y-2.5" aria-hidden />
        <div className="flex flex-col gap-2 pl-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 sm:pl-2.5">
          <div className="flex min-w-0 gap-2">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 shadow-inner ring-1 ring-emerald-200/60"
              title="Company"
            >
              <Building2 size={18} strokeWidth={1.75} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex max-w-full min-w-0 flex-wrap items-center gap-1.5">
                <h2 className="min-w-0 break-words text-lg font-medium leading-snug tracking-tight text-gray-700 sm:text-xl">
                  {companyName}
                </h2>
                {titleAfter}
              </div>
              <CompanyIntakeBadges closingDate={closingDate} workStatus={workStatus} className="mt-1.5" />
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-1 px-0.5 sm:max-w-xs sm:items-end sm:text-right">
            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-500" title="Your law firm tenant">
              <span className="inline-flex shrink-0 text-emerald-600" title="Law firm">
                <Landmark size={16} strokeWidth={2} aria-hidden />
              </span>
              Law firm
            </span>
            {lawFirmName ? (
              <span className="text-base font-medium leading-snug text-gray-700 sm:text-lg">{lawFirmName}</span>
            ) : null}
          </div>
        </div>
      </div>

      {showNotes ? (
        <div className="border-t border-gray-100/90 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="rounded-md border border-gray-100 bg-amber-50/30 px-2.5 py-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-amber-900/70">Notes</p>
            <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-gray-800">{notesTrim}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
